import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Typography,
  Collapse,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material'
import {
  SmartToy as AgentsIcon,
  People as UsersIcon,
  Science as TestRunsIcon,
  Email as EmailIcon,
  Storage as MemoryIcon,
  MenuBook as KnowledgeIcon,
  SettingsApplications as EnvironmentIcon,
  Psychology as ModelsIcon,
  Hub as ClusterIcon,
  Speed as ProfilingIcon,
  Schedule as JobsIcon,
  Dashboard as DashboardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess,
  ExpandMore,
  Home as HomeIcon,
  ManageSearch as TicketManagementIcon,
  Insights as DisputeInsightsIcon,
} from '@mui/icons-material'
import AutoAwesomeMosaicIcon from '@mui/icons-material/AutoAwesomeMosaic'

const DRAWER_WIDTH_OPEN = 280
const DRAWER_WIDTH_CLOSED = 72

const AdminLayout = ({ children, hiddenGroupIds = [] }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Load collapsed state from localStorage
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('adminNavCollapsed')
    return saved ? JSON.parse(saved) : false
  })

  const [expandedGroups, setExpandedGroups] = useState({
    system: true,
    'agent-builder': true,
    configuration: true,
    monitoring: true,
  })

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('adminNavCollapsed', JSON.stringify(collapsed))
  }, [collapsed])

  // Close drawer on mobile when route changes
  useEffect(() => {
    if (isMobile) {
      setCollapsed(true)
    }
  }, [location.pathname, isMobile])

  const toggleCollapsed = () => {
    setCollapsed(!collapsed)
  }

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }))
  }

  const navigationGroups = [
    {
      id: 'main',
      title: 'Main',
      hideTitle: true,
      items: [
        {
          title: 'Dashboard',
          icon: <DashboardIcon />,
          path: '/admin',
        },
      ]
    },
    {
      id: 'system',
      title: 'System',
      items: [
        {
          title: 'Agent Management',
          icon: <AgentsIcon />,
          path: '/admin/agents',
        },
        {
          title: 'User Management',
          icon: <UsersIcon />,
          path: '/admin/users',
        },
        {
          title: 'Cluster Management',
          icon: <ClusterIcon />,
          path: '/admin/cluster',
        },
        {
          title: 'Background Jobs',
          icon: <JobsIcon />,
          path: '/admin/jobs',
        },
        {
          title: 'Dispute Insights',
          icon: <DisputeInsightsIcon />,
          path: '/admin/dispute-insights',
        },
      ]
    },
    {
      id: 'agent-builder',
      title: 'Agent Builder',
      items: [
        {
          title: 'Agent Builder',
          icon: <AutoAwesomeMosaicIcon />,
          path: '/admin/agent-builder',
        },
      ]
    },
    {
      id: 'configuration',
      title: 'Configuration',
      items: [
        {
          title: 'LLM Models',
          icon: <ModelsIcon />,
          path: '/admin/models',
        },
        {
          title: 'Environment Config',
          icon: <EnvironmentIcon />,
          path: '/admin/environment',
        },
        {
          title: 'Email Management',
          icon: <EmailIcon />,
          path: '/admin/email-management',
        },
      ]
    },
    {
      id: 'monitoring',
      title: 'Monitoring & Tools',
      items: [
        {
          title: 'Ticket Management',
          icon: <TicketManagementIcon />,
          path: '/admin/ticket-management',
        },
        {
          title: 'Performance Profiling',
          icon: <ProfilingIcon />,
          path: '/admin/profiling',
        },
        {
          title: 'Test Runs',
          icon: <TestRunsIcon />,
          path: '/admin/test-runs',
        },
        {
          title: 'Memory Browser',
          icon: <MemoryIcon />,
          path: '/admin/memory',
        },
        {
          title: 'Knowledge Management',
          icon: <KnowledgeIcon />,
          path: '/admin/knowledge',
        },
      ]
    },
  ]

  const isActivePath = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin'
    }
    return location.pathname.startsWith(path)
  }

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 64,
        }}
      >
        {!collapsed && (
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Admin Panel
          </Typography>
        )}
        <IconButton onClick={toggleCollapsed} size="small">
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      <Divider />

      {/* Back to Tickets Link */}
      <Box sx={{ px: 1, pt: 2 }}>
        <Tooltip title={collapsed ? "Back to Tickets" : ""} placement="right">
          <ListItemButton
            onClick={() => navigate('/tickets')}
            sx={{
              minHeight: 48,
              justifyContent: collapsed ? 'center' : 'initial',
              px: collapsed ? 1 : 2,
              borderRadius: 1,
              bgcolor: 'action.hover',
              '&:hover': {
                bgcolor: 'action.selected',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: collapsed ? 0 : 2,
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <HomeIcon />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary="Back to Tickets"
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mt: 2 }} />

      {/* Navigation */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <List sx={{ px: 1, pt: 1 }}>
          {navigationGroups.filter(g => !hiddenGroupIds.includes(g.id)).map((group, groupIndex) => (
            <React.Fragment key={group.id}>
              {groupIndex > 0 && <Divider sx={{ my: 1 }} />}

              {/* Group Header (collapsible) */}
              {!group.hideTitle && !collapsed && (
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => toggleGroup(group.id)}
                    sx={{
                      minHeight: 36,
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    <ListItemText
                      primary={group.title}
                      primaryTypographyProps={{
                        variant: 'caption',
                        sx: {
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                        },
                      }}
                    />
                    {expandedGroups[group.id] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </ListItemButton>
                </ListItem>
              )}

              {/* Group Items */}
              <Collapse in={collapsed || expandedGroups[group.id]} timeout="auto">
                {group.items.map((item) => {
                  const isActive = isActivePath(item.path)
                  const listItem = (
                    <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => navigate(item.path)}
                        selected={isActive}
                        sx={{
                          minHeight: 44,
                          justifyContent: collapsed ? 'center' : 'initial',
                          px: collapsed ? 1 : 2,
                          borderRadius: 1,
                          '&.Mui-selected': {
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            '&:hover': {
                              bgcolor: 'primary.dark',
                            },
                            '& .MuiListItemIcon-root': {
                              color: 'primary.contrastText',
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: collapsed ? 0 : 2,
                            justifyContent: 'center',
                            color: isActive ? 'inherit' : 'text.secondary',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        {!collapsed && (
                          <ListItemText
                            primary={item.title}
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                              fontWeight: isActive ? 600 : 400,
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  )

                  // Wrap in tooltip when collapsed
                  return collapsed ? (
                    <Tooltip key={item.path} title={item.title} placement="right">
                      {listItem}
                    </Tooltip>
                  ) : (
                    listItem
                  )
                })}
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          width: collapsed ? DRAWER_WIDTH_CLOSED : DRAWER_WIDTH_OPEN,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: collapsed ? DRAWER_WIDTH_CLOSED : DRAWER_WIDTH_OPEN,
            boxSizing: 'border-box',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
            borderRight: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: `calc(100% - ${collapsed ? DRAWER_WIDTH_CLOSED : DRAWER_WIDTH_OPEN}px)`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

export default AdminLayout
